<template>
  <MUIDialog
    :class="{
      'mui-dialog--add-to-collection': mode === 'add',
      'mui-dialog--create-collection': mode === 'create'
    }"
    data-html2canvas-ignore="true"
    :acceptItem="acceptItem[mode]"
    :actionItem="actionItem[mode]"
    :dismissItem="dismissItem[mode]"
    :navItem="navItem[mode]"
    :displayType="displayType"
    :scrollable="true"
    :title="title[mode]"
    ref="dialog">
    <div class="mui-menu__container mui-menu__items mui-list" role="menu" aria-hidden="true" v-if="mode=='add'">
      <div class="mui-list-item mui-list-item--zero" v-if="hasCollections === false">
        You haven't added any collections.<br /> <span @click="mode = 'create'" class="read-more">Add&nbsp;one...</span>
      </div>
      <MUIListItem
        v-for="(collection, index) in collections.true"
        :key="collection.key"
        :class="{
          'mui-list-item--new': collection.key === newestCollectionKey,
          'mui-list-item--selected': isSelected(collection.key),
          'mui-list-item--last': index === collections.true.length - 1
        }"
        role="menuitem"
        :text="collection.name"
        @click.native="toggleCollection(collection.key)" />
      <MUIListItem
        v-for="(collection) in collections.false"
        :key="collection.key"
        :class="{
          'mui-list-item--new': collection.key === newestCollectionKey,
          'mui-list-item--selected': isSelected(collection.key)
        }"
        role="menuitem"
        :text="collection.name"
        @click.native="toggleCollection(collection.key)" />
    </div>
    <div class="form-group field-input" v-if="mode==='create'">
      <div class="field-wrap">
        <input
          name='newCollectionName'
          placeholder='Enter a collection name'
          ref='newCollectionNameInput'
          type='text'
          v-model.trim='newCollectionName' />
      </div>
      <span v-if="error.name" class="help is-danger">{{error.name[errors[0]]}}</span>
    </div>
  </MUIDialog>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  MUIDialog,
  MUIListItem
} from '@/components'
import {
  errorMessages
} from '@/utils'

import { APP } from '@/config'
import groupBy from 'lodash.groupby'
import * as Sentry from '@sentry/vue'

export default {
  components: {
    MUIDialog,
    MUIListItem
  },
  props: [
    'recipeKey'
  ],
  computed: {
    ...mapGetters([
      'isSizeClassCompact',
      'userCollections'
    ]),
    dismissItem () {
      return {
        'add': {
          icon: this.isSizeClassCompact ? 'keyboard_arrow_down' : null,
          name: 'Done',
          fn: () => { this.$refs.dialog.close() }
        }
      }
    },
    error () {
      return this.errors.length > 0 && errorMessages().collection
    },
    recipe () {
      return this.$store.state.Recipe.items[this.recipeKey]
    },
    selectedCollections () {
      return this.recipe.collectionKeys
    }
  },
  data () {
    return {
      acceptItem: {
        'create': {
          id: 'create_collection',
          fn: () => { this.createCollection() },
          name: 'Create'
        }
      },
      actionItem: {
        'add': {
          id: 'create_collection',
          fn: () => { this.mode = 'create' },
          icon: 'add',
          name: 'Create collection'
        }
      },
      collections: {},
      displayType: APP,
      errors: [],
      // eslint-disable-next-line
      hasCollections: undefined,
      mode: 'add',
      navItem: {
        'create': {
          id: 'back',
          fn: () => {
            this.resetForm()
            this.mode = 'add'
          },
          icon: 'navigate_before',
          name: 'Back'
        }
      },
      newCollectionName: '',
      newestCollectionKey: null,
      title: {
        'add': 'Add to...',
        'create': 'New Collection'
      }
    }
  },
  methods: {
    createCollection () {
      if (!this.newCollectionName) this.errors.push('required')
      if (this.errors.length > 0) return

      this.$store.dispatch('createCollection', {
        uid: this.$store.state.User.uid,
        name: this.newCollectionName.trim()
      }).then(collectionKey => {
        this.hasCollections = true
        this.toggleCollection(collectionKey, true)
        this.newestCollectionKey = collectionKey
        this.resetForm()
        this.mode = 'add'
        this.$track('collection.create', {
          context: 'collection',
          ability: 'create',
          collectionId: collectionKey,
          collectionName: this.newCollectionName
        })
      }).catch(error => {
        console.log(error)
        this.errors.push('unique')
      })
    },
    isSelected (key) {
      return this.recipe.collectionKeys.includes(key)
    },
    resetForm () {
      this.newCollectionName = null
      this.errors = []
    },
    setCollections () {
      let systemCollections = []
      let collections = systemCollections.concat(this.userCollections)
      this.hasCollections = true // this.userCollections.length > 0
      this.collections = groupBy(collections, (collection) => {
        return this.isSelected(collection.key)
      })
    },
    toggleCollection (collectionKey, resetCollections) {
      let type = this.isSelected(collectionKey)
        ? 'removeFromCollection'
        : 'addToCollection'
      let uid = this.$store.state.User.uid
      let payload = {
        type,
        uid,
        key: this.recipeKey,
        collectionKey }
      if (resetCollections) payload.callback = this.setCollections
      this.$store.dispatch(payload)
      try {
        this.$track(`recipe.${type}`, {
          context: 'recipe',
          ability: type,
          collectionId: collectionKey,
          collectionName: this.$store.state.Collection.collections[collectionKey].name,
          recipeId: this.recipeKey
        })
      } catch (err) {
        Sentry.captureException(err)
      }
    }
  },
  mounted () {
    this.setCollections()
  },
  watch: {
    mode () {
      if (this.mode === 'create') {
        this.$nextTick(() => {
          this.$refs.newCollectionNameInput.focus()
        })
      }
    },
    newCollectionName () {
      if (!this.newCollectionName) return
      Object.values(this.$store.state.Collection.collections).map(collection => {
        return collection.name.toUpperCase()
      }).indexOf(this.newCollectionName.toUpperCase()) > 0
        ? this.errors.push('unique')
        : this.errors.pop('unique')
      this.errors = [...new Set(this.errors)]
    }
  }
}
</script>

<style lang="sass" scope>
.mui-dialog.mui-dialog--add-to-collection
  .device__viewport-class--regular &
    width: 360px
  .mui-dialog__body
    padding: 0
  .mui-list
    padding: 0
    max-width: 100%
    width: 100%
  .mui-list-item
    position: relative
  .mui-list-item--selected
    &::before
      content: 'check'
      position: absolute
      font-family: 'Material Icons'
      font-feature-settings: 'liga'
      font-size: 20px
      right: 22px
  .mui-list-item--last
    border-bottom: 1px solid #e5e5e5
    &:last-child
      border-bottom: none
  .mui-list-item--zero
    color: #788695
    cursor: auto
    display: block
    height: auto
    padding: 24px
    text-align: center
    white-space: normal
    &:hover
      background: none
  .mui-list-item--new
    animation: notifyFade 4s
.mui-dialog.mui-dialog--create-collection
  .device__viewport-class--regular &
    width: 360px
@keyframes notifyFade
  from
    background: #FFFAE4
    color: #C39D02
  to
    background: transparent
    color: inherit
</style>



// WEBPACK FOOTER //
// src/views/AddToCollectionDialog.vue